import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import './sampleCancellation.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { CheckedCombo } from '../../../components/CheckedCombo';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { UsDateInput } from '../../../components/UsDateInput';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  apiMessage, cancelSamples, fetchCancellationAudit, fetchCancellationQueue, fetchReasonCodes, restoreSamples,
} from './api';
import { buildHistory, usedCounts, type HistoryRow } from './history';
import {
  FACETS, RANGES, emptyQuery, facetOptions, matches, querySummary, rangeDates, sameQuery,
  type FacetKey, type QueueQuery, type RangeKey,
} from './queueQuery';
import {
  BILLABLE_LABELS, STAGE_ENTRY, STAGE_PREP, rowKey,
  type CancellationChange, type CancellationQueueRow, type CancellationReasonCode,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Sample Tracking → Sample Cancellation (nav `samplecancel`).
// Prototype: Sample-Cancellation-v15.html.
//
// A sample TEST can be cancelled while it is Pending Sample Prep or Pending
// Result Entry — before any result is entered.  Cancelling sets the test's
// SampleStatus to "Cancelled" and puts it on hold (SampleParameter.TestHold);
// nothing is deleted and Restore puts it back.  The API does the work — this
// screen only selects, asks for the reason code, and confirms.
//
// The queue opens EMPTY (prototype v7): nothing loads until Search.
//
// QUERY PANEL (prototype v4/v5/v8).  The server filters only by the Date
// Received window; the other boxes cascade client-side (queueQuery.ts) — each
// lists only the values that fit what is picked in the others.  To offer those
// lists before Search, the rows for the DRAFT date window are fetched quietly in
// the background (`pool`); the grid only ever shows the last SEARCHED set (`rows`).
//
// WORKFLOW (prototype "How it works").  The API decides what a cancellation does
// to the queues and batches — removing the test from its prep batch, refusing
// one that has a result or sits in a QC batch (never touched: it is bound to its
// spreadsheet) — and tells this
// screen in words (`impact` on each queue row), which the Cancel dialog shows
// grouped by stage.  Nothing about batches is decided here.
//
// Reason codes are maintained in ALPACA (dbo.ReasonCodes) and shown read-only.
// History is derived from this form's audit rows (history.ts) — the reason code
// and comment live nowhere else.  The field-level audit is the shared audit log
// (title chip).
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Sample Cancellation';

type Tab = 'queue' | 'history' | 'reasons';
type Chip = 'open' | 'prep' | 'entry' | 'cancelled';

const QUEUE_COLUMNS: AppColumn<CancellationQueueRow>[] = [
  { field: 'sampleId',     header: 'Sample ID',   width: 140 },
  { field: 'sampleName',   header: 'Sample Name', width: 220 },
  { field: 'jobId',        header: 'Job ID',      width: 120 },
  { field: 'test',         header: 'Test',        width: 200 },
  { field: 'method',       header: 'Method',      width: 130 },
  { field: 'stage',        header: 'Stage',       width: 160 },
  { field: 'dateReceived', header: 'Received',    width: 140, type: 'datetime' },
  { field: 'status',       header: 'Status',      width: 160, type: 'status' },
];

interface ReasonRow extends CancellationReasonCode { appliesTo: string; billableText: string; commentReq: string; statusText: string; used: number }

const REASON_COLUMNS: AppColumn<ReasonRow>[] = [
  { field: 'code',         header: 'Code',              width: 100 },
  { field: 'reason',       header: 'Reason',            width: 320 },
  { field: 'appliesTo',    header: 'Applies to status', width: 280 },
  { field: 'commentReq',   header: 'Comment req.',      width: 110 },
  { field: 'billableText', header: 'Billable',          width: 120 },
  { field: 'used',         header: 'Used',              width: 70, align: 'right' },
  { field: 'sortOrder',    header: 'Sort',              width: 70, align: 'right' },
  { field: 'statusText',   header: 'Status',            width: 100, type: 'status' },
];

const HISTORY_COLUMNS: AppColumn<HistoryRow>[] = [
  { field: 'date',     header: 'Date',     width: 140, type: 'datetime' },
  { field: 'action',   header: 'Action',   width: 110, type: 'status' },
  { field: 'sampleId', header: 'Sample ID', width: 140 },
  { field: 'test',     header: 'Test',     width: 180 },
  { field: 'stage',    header: 'Stage',    width: 160 },
  { field: 'reason',   header: 'Reason',   width: 100 },
  { field: 'comment',  header: 'Comment',  width: 320 },
  { field: 'billable', header: 'Billable', width: 110 },
  { field: 'batch',    header: 'Removed from prep batch', width: 190 },
  { field: 'by',       header: 'By',       width: 140 },
];

/** What the cancel did beyond the status change, in one sentence — or '' for nothing. */
function batchSummary(rows: CancellationChange[]): string {
  const batches = [...new Set(rows.map((r) => r.prepBatchRemoved).filter(Boolean))];
  return batches.length ? ` Removed from prep batch ${batches.join(', ')}.` : '';
}

const FIND_FIELDS: (keyof CancellationQueueRow)[] =
  ['sampleId', 'sampleName', 'jobId', 'test', 'method', 'stage', 'status'];

export default function SampleCancellationPage() {
  const notify = useNotify();
  const [tab, setTab] = useState<Tab>('queue');

  // ── Queue ────────────────────────────────────────────────────────────────
  const [draft, setDraft]       = useState<QueueQuery>(() => emptyQuery());
  const [applied, setApplied]   = useState<QueueQuery | null>(null);   // null = not searched yet
  const [panelOpen, setPanelOpen] = useState(true);
  const [pool, setPool]         = useState<CancellationQueueRow[]>([]);   // draft-window rows, for the boxes
  const [poolTick, setPoolTick] = useState(0);   // bumped to re-read the boxes after the data changed
  const [rows, setRows]         = useState<CancellationQueueRow[]>([]);   // last searched set, for the grid
  const [loading, setLoading]   = useState(false);
  const [loadMs, setLoadMs]     = useState<number | null>(null);
  const [chip, setChip]         = useState<Chip>('open');
  const [find, setFind]         = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const datesInvalid = !!draft.from && !!draft.to && draft.from > draft.to;

  // Background fetch for the DRAFT date window — feeds the boxes' option lists.
  // Debounced (Custom dates are typed), quiet
  // (no global loader), and a stale answer is dropped.
  useEffect(() => {
    if (datesInvalid) { setPool([]); return; }
    let stale = false;
    const t = setTimeout(() => {
      fetchCancellationQueue(draft.from, draft.to, true)
        .then((r) => { if (!stale) setPool(r); })
        .catch((e) => { if (!stale) notify.error(apiMessage(e, 'Failed to load the query options.')); });
    }, 300);
    return () => { stale = true; clearTimeout(t); };
  }, [draft.from, draft.to, datesInvalid, notify, poolTick]);

  const load = useCallback(async (q: QueueQuery) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchCancellationQueue(q.from, q.to));
      setApplied(q);
      setSelected(new Set());
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the cancellation queue.'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  function search() {
    if (datesInvalid) { notify.warning('"Date Received from" is after "to".'); return; }
    setPanelOpen(false);
    // A new search starts on the All chip with no quick-find text: a chip or a
    // find box left over from the previous result can hide every row of this one.
    setChip('open'); setFind('');
    void load(draft);
  }

  function clearQuery() {
    setDraft(emptyQuery()); setApplied(null); setRows([]); setSelected(new Set());
    setChip('open'); setFind(''); setPanelOpen(true);
  }

  function setRange(range: RangeKey) {
    // Custom keeps whatever dates are showing so they can be edited from there.
    setDraft((d) => ({ ...d, range, ...(range === 'Custom' ? {} : rangeDates(range)) }));
  }

  const setPick = (k: FacetKey, values: string[]) =>
    setDraft((d) => ({ ...d, picks: { ...d.picks, [k]: values } }));

  const options = useMemo(() => {
    const o = {} as Record<FacetKey, string[]>;
    for (const f of FACETS) o[f.key] = facetOptions(pool, draft, f.key);
    return o;
  }, [pool, draft]);

  // The searched set, narrowed by the APPLIED boxes (not the draft being edited).
  const matched = useMemo(() => (applied ? rows.filter((r) => matches(r, applied)) : []), [rows, applied]);

  const counts = useMemo(() => ({
    open:      matched.filter((r) => !r.isCancelled).length,
    prep:      matched.filter((r) => !r.isCancelled && r.stage === STAGE_PREP).length,
    entry:     matched.filter((r) => !r.isCancelled && r.stage === STAGE_ENTRY).length,
    cancelled: matched.filter((r) => r.isCancelled).length,
  }), [matched]);

  const visible = useMemo(() => {
    const q = find.trim().toLowerCase();
    return matched.filter((r) =>
      (chip === 'cancelled' ? r.isCancelled
        : !r.isCancelled && (chip === 'open' || r.stage === (chip === 'prep' ? STAGE_PREP : STAGE_ENTRY)))
      && (!q || FIND_FIELDS.some((f) => String(r[f] ?? '').toLowerCase().includes(q))));
  }, [matched, chip, find]);

  const picked = useMemo(() => visible.filter((r) => selected.has(rowKey(r))), [visible, selected]);
  const toCancel  = picked.filter((r) => !r.isCancelled);
  const toRestore = picked.filter((r) => r.isCancelled);

  // ── Reason codes ─────────────────────────────────────────────────────────
  const [codes, setCodes]               = useState<CancellationReasonCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const loadCodes = useCallback(async () => {
    setCodesLoading(true);
    try { setCodes(await fetchReasonCodes(false)); }
    catch (e) { notify.error(apiMessage(e, 'Failed to load reason codes.')); }
    finally { setCodesLoading(false); }
  }, [notify]);

  useEffect(() => { void loadCodes(); }, [loadCodes]);

  // ── History (derived from this form's audit rows) ────────────────────────
  const [history, setHistory]               = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyFind, setHistoryFind]       = useState('');

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try { setHistory(buildHistory(await fetchCancellationAudit())); }
    catch (e) { notify.error(apiMessage(e, 'Failed to load the cancellation history.')); }
    finally { setHistoryLoading(false); }
  }, [notify]);

  useEffect(() => { void loadHistory(); }, [loadHistory]);

  const historyRows = useMemo(() => {
    const q = historyFind.trim().toLowerCase();
    return q ? history.filter((h) => Object.values(h).some((v) => String(v ?? '').toLowerCase().includes(q))) : history;
  }, [history, historyFind]);

  const used = useMemo(() => usedCounts(history), [history]);

  const reasonRows = useMemo<ReasonRow[]>(() => codes
    .filter((c) => showInactive || c.active)
    .map((c) => ({
      ...c,
      appliesTo: [c.pendingSamplePrep && STAGE_PREP, c.pendingResultEntry && STAGE_ENTRY].filter(Boolean).join(', '),
      billableText: BILLABLE_LABELS[c.billable] ?? String(c.billable),
      commentReq: c.commentRequired ? 'Yes' : 'No',
      statusText: c.active ? 'Active' : 'Inactive',
      used: used[c.code ?? ''] ?? 0,
    })), [codes, showInactive, used]);

  // Cancel dialog: the selection grouped by stage, each with what cancelling does.
  const impactGroups = useMemo(() => [STAGE_PREP, STAGE_ENTRY].map((stage) => {
    const tests = toCancel.filter((r) => r.stage === stage);
    return { stage, tests, impacts: [...new Set(tests.map((r) => r.impact).filter(Boolean))] as string[] };
  }).filter((g) => g.tests.length > 0), [toCancel]);

  // ── Cancel dialog ────────────────────────────────────────────────────────
  const [dialog, setDialog]   = useState<'cancel' | 'restore' | null>(null);
  const [reasonOid, setReasonOid] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy]       = useState(false);

  // Only ACTIVE codes that apply to EVERY stage in the selection (prototype v10).
  const offered = useMemo(() => {
    const needPrep  = toCancel.some((r) => r.stage === STAGE_PREP);
    const needEntry = toCancel.some((r) => r.stage === STAGE_ENTRY);
    return codes.filter((c) => c.active && (!needPrep || c.pendingSamplePrep) && (!needEntry || c.pendingResultEntry));
  }, [codes, toCancel]);
  const reason = offered.find((c) => c.oid === reasonOid);
  const commentMissing = dialog === 'restore' ? !comment.trim() : !!reason?.commentRequired && !comment.trim();

  function openDialog(kind: 'cancel' | 'restore') {
    setReasonOid(''); setComment(''); setDialog(kind);
  }

  function pickReason(oid: string) {
    setReasonOid(oid);
    // Pre-fill the code's default comment, but never over something already typed.
    const def = offered.find((c) => c.oid === oid)?.defaultComment;
    if (def && !comment.trim()) setComment(def);
  }

  async function submit() {
    if (busy || commentMissing || (dialog === 'cancel' && !reason)) return;
    const acting = dialog === 'cancel' ? toCancel : toRestore;
    const items = acting.map((r) => ({ sampleOid: r.sampleOid, testMethodOid: r.testMethodOid }));
    setBusy(true);
    try {
      const res = dialog === 'cancel'
        ? await cancelSamples(items, reasonOid, comment.trim())
        : await restoreSamples(items, comment.trim());
      const verb = dialog === 'cancel' ? 'Cancel' : 'Restore';
      setDialog(null);
      const qc = new Set(res.rows.flatMap((r) => (r.qcRemoved ?? '').split(', ').filter(Boolean))).size;
      const qcNote = qc > 0 ? ` ${qc} QC row(s) made from the cancelled sample(s) were removed with them.` : '';
      const skipped = res.changed < res.requested
        ? ` ${res.changed} of ${res.requested} — the rest were skipped (results entered, in a QC batch, or already updated).` : '';
      if (res.changed === 0) notify.info('Nothing changed — those sample tests have results entered, are in a QC batch, or were already updated.');
      else notify.success(`${verb} completed successfully.${skipped}${batchSummary(res.rows)}${qcNote}`);
      void loadHistory();
      setPoolTick((n) => n + 1);          // the boxes' option lists changed too
      if (applied) await load(applied);
    } catch (e) {
      notify.error(apiMessage(e, 'The request failed.'));
    } finally {
      setBusy(false);
    }
  }

  const searched = applied !== null;
  // The boxes were edited after the last Search — the grid still shows the old query.
  const draftPending = applied !== null && !sameQuery(draft, applied);
  // Reloads the grid AND the boxes' option lists, which come from their own fetch.
  const refresh = () => { setPoolTick((n) => n + 1); if (applied) void load(applied); };
  const emptyText = !searched
    ? 'No data loaded. Set the query above (Date Range is 1M by default) and click Search.'
    : matched.length === 0
      ? 'No sample tests match the query.'
      : `None of the ${matched.length} matching sample test${matched.length === 1 ? '' : 's'} ${
          find.trim() ? 'fit the quick find' : 'are under this status'}. Choose All above${find.trim() ? ' or clear the quick find' : ''}.`;

  return (
    <div className="bl-page">
      <FormTitle pageId="samplecancel" loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />

      <div className="sc-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'queue'}
                className={tab === 'queue' ? 'on' : ''} onClick={() => setTab('queue')}>
          Cancellation Queue
        </button>
        <button type="button" role="tab" aria-selected={tab === 'history'}
                className={tab === 'history' ? 'on' : ''} onClick={() => setTab('history')}>
          History
        </button>
        <button type="button" role="tab" aria-selected={tab === 'reasons'}
                className={tab === 'reasons' ? 'on' : ''} onClick={() => setTab('reasons')}>
          Reason Codes
        </button>
      </div>

      {tab === 'queue' ? (
        <>
          {/* Query — filter-like, so it never counts as an unsaved edit (rule 15). */}
          <div className={`sc-qpanel${panelOpen ? ' open' : ''}`} data-unsaved-ignore>
            <button type="button" className="sc-qhead" aria-expanded={panelOpen}
                    onClick={() => setPanelOpen((o) => !o)}>
              <i className="ti ti-chevron-right" aria-hidden="true" />
              Query
              <span className="sc-qsum">
                {applied
                  ? <>{querySummary(applied, fmtLocalDate)} <b>· {matched.length} sample test{matched.length === 1 ? ' matches' : 's match'}</b></>
                  : <>Set the criteria and click <b>Search</b> — nothing is loaded yet</>}
              </span>
            </button>
            <form className="sc-qbody" onSubmit={(e) => { e.preventDefault(); search(); }}>
              <div className="sc-qgrid">
                <label className="sc-qf"><span>Date Range</span>
                  <select className="sc-input" value={draft.range}
                          onChange={(e) => setRange(e.target.value as RangeKey)}>
                    {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                </label>
                {/* Filled by the preset and read-only; editable only for Custom (prototype v5). */}
                <label className="sc-qf"><span>Date Received from</span>
                  <UsDateInput className="sc-input" value={draft.from} readOnly={draft.range !== 'Custom'}
                               onChange={(from) => setDraft((d) => ({ ...d, from }))} />
                </label>
                <label className="sc-qf"><span>Date Received to</span>
                  <UsDateInput className="sc-input" value={draft.to} readOnly={draft.range !== 'Custom'}
                               min={draft.from || undefined}
                               onChange={(to) => setDraft((d) => ({ ...d, to }))} />
                </label>
                {FACETS.map((f) => (
                  <Fragment key={f.key}>
                    <div className="sc-qf"><span>{f.label}</span>
                      <CheckedCombo options={options[f.key]} values={draft.picks[f.key]}
                                    onChange={(v) => setPick(f.key, v)}
                                    placeholder="All" showCount ariaLabel={f.label} />
                    </div>
                    {f.key === 'siteName' && (
                      <label className="sc-qf"><span>Sample Name <em>(contains)</em></span>
                        <input className="sc-input" value={draft.sampleName} maxLength={200} placeholder="e.g. FRB"
                               onChange={(e) => setDraft((d) => ({ ...d, sampleName: e.target.value }))} />
                      </label>
                    )}
                  </Fragment>
                ))}
                {/* Last grid cell, not a row of its own — keeps the panel short. */}
                <div className="sc-qfoot">
                  {draftPending && <span className="sc-qpending">Changed — click Search to apply</span>}
                  <button type="button" className="ca-btn" disabled={loading} onClick={clearQuery}>Clear</button>
                  <button type="submit" className="ca-btn" disabled={loading}>Search</button>
                </div>
              </div>
            </form>
          </div>

          <AppToolbar
            left={
              <>
                {/* The screen's ONE primary action — solid accent, as in the mockup. */}
                <button type="button" className="ca-btn ca-btn-solid" disabled={loading || toCancel.length === 0}
                        onClick={() => openDialog('cancel')}>
                  <i className="ti ti-ban" aria-hidden="true" /> Cancel Sample
                </button>
                <button type="button" className="ca-btn" disabled={loading || toRestore.length === 0}
                        onClick={() => openDialog('restore')}>
                  <i className="ti ti-arrow-back-up" aria-hidden="true" /> Restore
                </button>
                {/* The per-test detail view (prototype "Open") is not built yet. */}
                <button type="button" className="ca-btn" disabled title="Detail view — not built yet">
                  <i className="ti ti-external-link" aria-hidden="true" /> Open
                </button>
              </>
            }
            onRefresh={refresh}
            refreshing={loading}
            refreshDisabled={!applied}
            exportData={{ filename: 'sample-cancellation', ...gridExportMatrix(visible, QUEUE_COLUMNS) }}
          />

          <div className="sc-filterrow">
          <div className="sc-chips">
            {([['open', 'All'], ['prep', STAGE_PREP], ['entry', STAGE_ENTRY], ['cancelled', 'Cancelled']] as [Chip, string][])
              .map(([k, label]) => (
                <button key={k} type="button" className={chip === k ? 'on' : ''}
                        onClick={() => { setChip(k); setSelected(new Set()); }}>
                  {label}<b>{counts[k]}</b>
                </button>
              ))}
          </div>
          <GridSearch value={find} onChange={setFind} placeholder="Quick find in results…" />
          </div>

          <AppDataGrid<CancellationQueueRow>
            rows={visible}
            columns={QUEUE_COLUMNS}
            getRowId={rowKey}
            loading={loading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            emptyText={emptyText}
          />
        </>
      ) : tab === 'history' ? (
        <>
          <AppToolbar
            onRefresh={loadHistory}
            refreshing={historyLoading}
            exportData={{ filename: 'sample-cancellation-history', ...gridExportMatrix(historyRows, HISTORY_COLUMNS) }}
          />
          <div className="sc-filterrow" data-unsaved-ignore>
            <span />
            <GridSearch value={historyFind} onChange={setHistoryFind} placeholder="Search sample, test, reason, comment…" />
          </div>
          <AppDataGrid<HistoryRow>
            rows={historyRows}
            columns={HISTORY_COLUMNS}
            getRowId={(r) => r.key}
            loading={historyLoading}
            emptyText="No cancellations or restores yet."
          />
        </>
      ) : (
        <>
          <AppToolbar
            left={
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5 }} data-unsaved-ignore>
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                Show inactive
              </label>
            }
            onRefresh={loadCodes}
            refreshing={codesLoading}
            exportData={{ filename: 'cancellation-reason-codes', ...gridExportMatrix(reasonRows, REASON_COLUMNS) }}
          />
          <p className="sc-note">
            The reason codes offered when cancelling. They are maintained in ALPACA
            (Settings › Utility › Sample Tracking › Sample Cancellation › Reason Codes).
          </p>
          <AppDataGrid<ReasonRow>
            rows={reasonRows}
            columns={REASON_COLUMNS}
            getRowId={(r) => r.oid}
            loading={codesLoading}
            emptyText="No reason codes."
          />
        </>
      )}

      <AppDialog
        open={dialog !== null}
        title={dialog === 'cancel'
          ? `Cancel ${toCancel.length} sample test${toCancel.length === 1 ? '' : 's'}?`
          : `Restore ${toRestore.length} sample test${toRestore.length === 1 ? '' : 's'}?`}
        icon={dialog === 'cancel' ? 'ti-ban' : 'ti-arrow-back-up'}
        width={520}
        onClose={() => { if (!busy) setDialog(null); }}
        footer={
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={() => void submit()}
                    disabled={busy || commentMissing || (dialog === 'cancel' && !reason)}>
              {dialog === 'cancel' ? 'Yes, cancel' : 'Yes, restore'}
            </button>
            <button type="button" className="ca-btn" onClick={() => setDialog(null)} disabled={busy}>No</button>
          </>
        }
      >
        {/* Transient dialog inputs — committed or discarded with the dialog (rule 15). */}
        <div className="sc-dlg" data-unsaved-ignore>
          {dialog === 'cancel' ? (
            <>
              <p>The tests leave their queue as <b>Cancelled</b>. Nothing is deleted and each can be restored.</p>
              {impactGroups.map((g) => (
                <div className="sc-imp" key={g.stage}>
                  <b>{g.stage} — {g.tests.length} test{g.tests.length === 1 ? '' : 's'}</b>
                  <div>
                    {g.impacts.map((t) => <div key={t}>• {t}</div>)}
                    <small>{g.tests.map((r) => `${r.sampleId} (${r.test})`).join(', ')}</small>
                  </div>
                </div>
              ))}
              <label htmlFor="sc-reason">Reason code<span className="sc-req">*</span></label>
              <select id="sc-reason" className={reasonOid ? '' : 'sc-empty'} value={reasonOid}
                      onChange={(e) => pickReason(e.target.value)}>
                <option value="">— select —</option>
                {offered.map((c) => <option key={c.oid} value={c.oid}>{c.code} · {c.reason}</option>)}
              </select>
              <label htmlFor="sc-comment">Comment{reason?.commentRequired && <span className="sc-req">*</span>}</label>
            </>
          ) : (
            <>
              <p>
                Each test goes back to its queue. A test that was removed from a prep batch returns to the
                prep queue, not into the batch. Check hold time before analyzing.
              </p>
              <ul>{toRestore.map((r) => <li key={rowKey(r)}>{r.sampleId} — {r.test} → {r.stage}</li>)}</ul>
              <label htmlFor="sc-comment">Reason<span className="sc-req">*</span></label>
            </>
          )}
          <textarea id="sc-comment" className={commentMissing ? 'sc-empty' : ''} value={comment} maxLength={1000}
                    onChange={(e) => setComment(e.target.value)} />
        </div>
      </AppDialog>
    </div>
  );
}
